import { useEffect, useState } from "react";
import axios from "axios";
import { Minus, Plus, Trash2, ArrowUpRight, ArrowLeft } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (pence) => `£${(pence / 100).toFixed(2)}`;

const FREE_DELIVERY = 3000;
const DELIVERY_FEE = 395;
const GIFT_TSHIRT = 5000;
const GIFT_CAP = 6000;
const DIP_KEYS = ["mango_dip", "bombay_mix_pot"];
const DIP_UNITS = { mango_dip: 1, bombay_mix_pot: 1, dip_deal: 6 };

const GIFT_TEES = [
  { key: "tee_fit_black", name: "Proppa Fit — Black", image: "/merch/proppa-fit-black.jpg" },
  { key: "tee_fit_white", name: "Proppa Fit — White", image: "/merch/proppa-fit-white.jpg" },
  { key: "tee_bemy_black", name: "Be My Proppadom — Black", image: "/merch/be-my-proppadom-black.jpg" },
  { key: "tee_bemy_white", name: "Be My Proppadom — White", image: "/merch/be-my-proppadom-white.jpg" },
];

const UPSELL_PRIORITY = [
  "dip_deal", "original_24", "butter_chicken_24",
  "tamarind_24", "mango_dip", "bombay_mix_pot",
  "mixed_case_24", "snack_pack",
];

export const CartDrawer = () => {
  const { items, open, setOpen, setQuantity, removeItem, addItem, subtotal } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [giftChoice, setGiftChoice] = useState("tee_fit_black");
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/products`).then((res) => setCatalog(res.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const inBasket = new Set(items.map((i) => i.lookup_key));
  const suggestions = UPSELL_PRIORITY
    .map((k) => catalog.find((p) => p.lookup_key === k))
    .filter((p) => p && !inBasket.has(p.lookup_key) && (p.stock ?? 1) > 0 && !p.coming_soon)
    .slice(0, 3);

  const catalogMap = Object.fromEntries(catalog.map((p) => [p.lookup_key, p]));
  const availabilityIssue = (key) => {
    const c = catalogMap[key];
    if (!c) return null;
    if (c.coming_soon) return "Coming soon";
    if (c.stock !== null && c.stock !== undefined && c.stock <= 0) return "Sold out";
    return null;
  };
  const blockedItems = items.filter((i) => !i.free && availabilityIssue(i.lookup_key));

  const dipOnly = items.length > 0 && items.every((i) => i.lookup_key in DIP_UNITS);
  const dipCount = items.reduce((s, i) => s + (DIP_UNITS[i.lookup_key] || 0) * i.quantity, 0);
  const dipsBlocked = dipOnly && dipCount < 6;
  const deliveryFee = subtotal >= FREE_DELIVERY ? 0 : dipOnly ? 250 : DELIVERY_FEE;

  const continueShopping = () => {
    setOpen(false);
    setTimeout(() => document.querySelector("#range")?.scrollIntoView({ behavior: "smooth" }), 350);
  };

  const checkout = async () => {    setCheckingOut(true);
    setCheckoutError("");
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
        gift_choice: subtotal >= GIFT_TSHIRT ? giftChoice : null,
        email: checkoutEmail.trim() || null,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setCheckoutError(e.response?.data?.detail || "Checkout failed to start — please try again.");
      setCheckingOut(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-white flex flex-col p-0 border-l-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-[#E5DFD3]">
          <SheetTitle className="font-serif text-2xl font-bold tracking-tight">Your basket</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="mt-8">
              <p className="text-sm font-light text-[#1A1A1A]/50" data-testid="cart-empty-message">
                Nothing in here yet. That's not very proppa of you.
              </p>
              <button
                onClick={continueShopping}
                data-testid="continue-shopping-button"
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#2B0B3C] hover:text-[#4A148C] transition-colors"
              >
                <ArrowLeft size={15} strokeWidth={2.5} /> Continue shopping the range
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.lookup_key} className="flex gap-3" data-testid={`cart-item-${item.lookup_key}`}>
                  <div className="w-16 h-16 bg-[#EFEBE1] border border-[#E5DFD3] rounded-xl shrink-0 overflow-hidden">
                    <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="font-serif text-base font-medium leading-tight">{item.name}</p>
                      {!item.free && availabilityIssue(item.lookup_key) && (
                        <p className="text-[11px] font-bold text-[#C40061] mt-0.5" data-testid={`cart-blocked-${item.lookup_key}`}>
                          {availabilityIssue(item.lookup_key)} — remove to check out
                        </p>
                      )}
                      <button
                        onClick={() => removeItem(item.lookup_key)}
                        aria-label={`Remove ${item.name}`}
                        data-testid={`cart-remove-${item.lookup_key}`}
                        className="text-[#1A1A1A]/40 hover:text-[#4A148C] transition-colors duration-200"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <p className="text-sm font-bold text-[#1A1A1A]/70">{gbp(item.price_pence)}</p>
                    <div className="inline-flex items-center border-2 border-[#2B0B3C]/15 rounded-full bg-white">
                      <button
                        onClick={() => setQuantity(item.lookup_key, item.quantity - 1)}
                        aria-label="Decrease quantity"
                        data-testid={`cart-decrease-${item.lookup_key}`}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#FFD600] transition-colors duration-200"
                      >
                        <Minus size={15} strokeWidth={2.5} />
                      </button>
                      <span className="w-10 text-center text-sm font-bold" data-testid={`cart-qty-${item.lookup_key}`}>{item.quantity}</span>
                      <button
                        onClick={() => setQuantity(item.lookup_key, item.quantity + 1)}
                        aria-label="Increase quantity"
                        data-testid={`cart-increase-${item.lookup_key}`}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#FFD600] transition-colors duration-200"
                      >
                        <Plus size={15} strokeWidth={2.5} />
                      </button>
                    </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {suggestions.length > 0 && (
            <div className="mt-6 pt-4 border-t border-[#E5DFD3]" data-testid="cart-upsell">
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#2B0B3C]/60 mb-4">
                {items.length === 0 ? "Popular right now" : "Fancy something else?"}
              </p>
              <div className="space-y-2.5">
                {suggestions.map((p) => (
                  <div key={p.lookup_key} className="flex items-center gap-3" data-testid={`upsell-${p.lookup_key}`}>
                    <div className="w-11 h-11 bg-[#F3F0FF] rounded-xl shrink-0 overflow-hidden">
                      <img src={p.image} alt={p.name} className="w-full h-full object-contain mix-blend-multiply" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#2B0B3C] truncate">{p.name}</p>
                      <p className="text-xs font-medium text-[#2B0B3C]/50">{gbp(p.price_pence)}</p>
                    </div>
                    <button
                      onClick={() => addItem(p, false)}
                      data-testid={`upsell-add-${p.lookup_key}`}
                      aria-label={`Add ${p.name} to basket`}
                      className="w-9 h-9 rounded-full bg-[#2B0B3C] text-white flex items-center justify-center hover:bg-[#FF6B00] transition-colors duration-200 shrink-0"
                    >
                      <Plus size={15} strokeWidth={3} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="px-5 py-4 border-t border-[#E5DFD3] bg-[#EFEBE1]/50">
            {dipOnly ? (
              <p className="mb-3 text-xs font-medium text-[#2B0B3C]/60" data-testid="dips-delivery-note">
                Dip pots only — {subtotal >= FREE_DELIVERY ? (
                  <>nice one, <span className="font-bold text-[#4A148C]">free delivery</span> over £30.</>
                ) : (
                  <>flat <span className="font-bold text-[#2B0B3C]">£2.50</span> delivery, free over £30.</>
                )}
              </p>
            ) : subtotal < FREE_DELIVERY ? (
              <div className="mb-3" data-testid="free-delivery-progress">
                <p className="text-xs font-light text-[#1A1A1A]/60 mb-2">
                  <span className="font-medium text-[#1A1A1A]">{gbp(FREE_DELIVERY - subtotal)}</span> away from free delivery
                </p>
                <div className="h-1 bg-[#E5DFD3] w-full">
                  <div className="h-1 bg-[#4A148C] transition-[width] duration-500" style={{ width: `${Math.min(100, (subtotal / FREE_DELIVERY) * 100)}%` }} />
                </div>
              </div>
            ) : (
              <p className="mb-3 text-xs font-semibold tracking-[0.15em] uppercase text-[#4A148C]" data-testid="free-delivery-unlocked">
                Free delivery unlocked
              </p>
            )}
            {subtotal >= GIFT_TSHIRT ? (
              <div className="mb-3" data-testid="gift-picker">
                <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#4A148C] mb-2">
                  {subtotal >= GIFT_CAP ? "Free T-shirt + Cap — pick your tee" : "Free T-shirt — pick your design"}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {GIFT_TEES.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setGiftChoice(t.key)}
                      data-testid={`gift-pick-${t.key}`}
                      aria-label={`Choose ${t.name}`}
                      className={`border overflow-hidden transition-colors duration-200 ${
                        giftChoice === t.key ? "border-[#4A148C] ring-1 ring-[#4A148C]" : "border-[#E5DFD3] hover:border-[#1A1A1A]/40"
                      }`}
                    >
                      <img src={t.image} alt={t.name} className="w-full h-12 object-cover" />
                    </button>
                  ))}
                </div>
                <p className="text-[10px] font-light text-[#1A1A1A]/50 mt-2">
                  {GIFT_TEES.find((t) => t.key === giftChoice)?.name} · we confirm your size by email after ordering.
                  {subtotal < GIFT_CAP && ` ${gbp(GIFT_CAP - subtotal)} more adds the Cap.`}
                </p>
              </div>
            ) : (
              <p className="mb-3 text-xs font-light text-[#1A1A1A]/60" data-testid="gift-status">
                {`Spend ${gbp(GIFT_TSHIRT)} for a free T-shirt — ${gbp(GIFT_CAP)} adds a Cap too.`}
              </p>
            )}
            <div className="space-y-0.5 mb-2 text-sm font-light">
              <div className="flex justify-between">
                <span className="text-[#1A1A1A]/60">Subtotal</span>
                <span data-testid="cart-subtotal">{gbp(subtotal)}</span>
              </div>
              <div className="flex justify-between" data-testid="cart-delivery-row">
                <span className="text-[#1A1A1A]/60">Delivery</span>
                <span>{deliveryFee === 0 ? "FREE" : gbp(deliveryFee)}</span>
              </div>
            </div>
            <div className="flex justify-between items-baseline mb-1.5">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A1A1A]/60">Total</span>
              <span className="font-serif text-xl font-bold" data-testid="cart-total">
                {gbp(subtotal + deliveryFee)}
              </span>
            </div>
            <p className="text-[11px] font-light text-[#1A1A1A]/50 mb-3">Tax at checkout · PROPPA10 = 10% off first order</p>
            <input
              type="email"
              value={checkoutEmail}
              onChange={(e) => setCheckoutEmail(e.target.value)}
              placeholder="Email (optional — for your receipt & order updates)"
              data-testid="checkout-email-input"
              className="w-full h-11 rounded-full border border-[#1A1A1A]/15 bg-white px-5 mb-3 text-sm font-medium text-[#1A1A1A] placeholder:text-[#1A1A1A]/35 outline-none focus:border-[#4A148C] transition-colors duration-200"
            />
            {dipsBlocked && (
              <p className="text-xs font-medium text-[#4A148C] mb-4" data-testid="dips-minimum-note">
                Dips-only box needs a minimum of 6 — you have {dipCount}. Mix any flavours, or add a bundle and there's no minimum.
              </p>
            )}
            {blockedItems.length > 0 && (
              <p className="text-xs font-medium text-[#C40061] mb-4" data-testid="basket-blocked-note">
                Your basket has {blockedItems.length === 1 ? "an item that's" : "items that are"} no longer available — remove {blockedItems.length === 1 ? "it" : "them"} to check out.
              </p>
            )}
            {checkoutError && (
              <p className="text-xs text-[#4A148C] mb-4" data-testid="checkout-error">{checkoutError}</p>
            )}
            <button
              onClick={checkout}
              disabled={checkingOut || dipsBlocked || blockedItems.length > 0}
              data-testid="checkout-button"
              className="group w-full inline-flex items-center justify-center gap-3 bg-[#2B0B3C] text-white rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wide shadow-lg hover:bg-[#FF6B00] hover:-translate-y-0.5 disabled:opacity-50 transition-[transform,background-color] duration-200"
            >
              {checkingOut ? "Opening checkout…" : "Checkout securely"}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <button
              onClick={continueShopping}
              data-testid="continue-shopping-footer-button"
              className="mt-2.5 pb-1 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-[#2B0B3C]/60 hover:text-[#4A148C] transition-colors duration-200"
            >
              <ArrowLeft size={15} strokeWidth={2.5} /> Continue shopping the range
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
